import { useCallback, useContext, useMemo } from 'react'
import delay from 'lodash/delay'
import throttle from 'lodash/throttle'

import type { ProductVariant } from 'data/product'
import { trackError } from 'lib/errorTracker'
import { getNextPrevIndex } from 'lib/iterable'
import { ProductVariantContext } from 'components/ProductVariantProvider'

const DELAY_VARIANT_CHANGE_MS = 500
const THROTTLE_MS = 2500

export const useCurrentVariant = (withoutDelay = false) => {
  const {
    currentVariant,
    product: { variants },
    setVariant,
  } = useContext(ProductVariantContext)

  const setCurrentVariant = useMemo(
    () =>
      throttle(
        (variant: ProductVariant) => {
          // begin bottle loading state
          dispatchEvent(new Event('changeVariantStart'))

          delay(
            () => {
              setVariant(variant)
            },
            !withoutDelay ? DELAY_VARIANT_CHANGE_MS : 0
          )
        },
        !withoutDelay ? THROTTLE_MS : 0
      ),
    [setVariant, withoutDelay]
  )

  const { nextIndex, prevIndex } = useMemo(() => {
    const variantIds = variants.map((v) => v.id)
    const currentIndex = variantIds.indexOf(currentVariant.id)

    return getNextPrevIndex({
      setLength: variants.length,
      currentIndex,
    })
  }, [currentVariant.id, variants])

  const setNext = useCallback(() => {
    const nextVariant = variants[nextIndex]

    if (!nextVariant) {
      trackError('Expected to find next variant')
      return
    }

    setCurrentVariant(nextVariant)
  }, [nextIndex, setCurrentVariant, variants])

  const setPrevious = useCallback(() => {
    const previousVariant = variants[prevIndex]

    if (!previousVariant) {
      trackError('Expected to find previous variant')
      return
    }

    setCurrentVariant(previousVariant)
  }, [prevIndex, setCurrentVariant, variants])

  return {
    currentVariant,
    setVariant: setCurrentVariant,
    setPrevious,
    setNext,
    variants: variants.map((v) => ({
      ...v,
      active: currentVariant.id === v.id,
    })),
  }
}
